import React, { useEffect, useMemo, useState } from "react";
import { BuilderInfo, JobDetailsProps } from "../../providers/types";
import httpClient from "../../utils/httpClient";
import { useMsal } from "@azure/msal-react";
import { useData } from "../../providers/DataProvider";
import BuilderSplitTable from "./BuilderSplitTable";
import {
    BuilderSplitCommentRecord,
    BuilderSplitRow,
    BuilderSplitSnapshotRow,
} from "../BuilderSplitShared/types";
import DeductionDialog from "../BuilderSplitShared/DeductionDialog";
import {
    buildStructuredBuilderSplitComment,
    getLatestRelevantBuilderSplitComment,
    parseBuilderSplitComment,
} from "../BuilderSplitShared/builderSplitCommentParser";
import {
    computeSplitAmount,
    createBuilderSplitSnapshot,
    getChangedDeductionRows,
    hasAnyMeaningfulChanges,
    hydrateRowsWithParsedComments,
} from "../BuilderSplitShared/builderSplitHelpers";
import { getDeductionChanges, getRowsMissingDeductionComment, hasInvalidDeduction } from "../BuilderSplitShared/builderSplitValidation";

interface BuilderSplitWidgetProps {
    job: JobDetailsProps;
    jobBuilders?: BuilderInfo[];
    allBuilders?: {
        builderId: number;
        builderEmail: string;
        builderName: string;
        builderNumber: number;
        employeeNumber: number;
    }[];
    active: boolean;
    viewOnly?: boolean;
    setJobBuilders: React.Dispatch<React.SetStateAction<BuilderInfo[]>>;
    reloadBuilders: () => void;
}

const BuilderSplitWidget: React.FC<BuilderSplitWidgetProps> = ({
    job,
    jobBuilders = [],
    allBuilders = [],
    active,
    viewOnly,
    reloadBuilders,
}) => {
    const { accounts } = useMsal();
    const { role, bonusType } = useData();

    const [open, setOpen] = useState<boolean>(false);
    const [builderSplits, setBuilderSplits] = useState<BuilderSplitRow[]>([]);
    const [originalBuilderSplits, setOriginalBuilderSplits] = useState<BuilderSplitSnapshotRow[]>([]);
    const [baseComments, setBaseComments] = useState<string>("");
    const [unassignedBonusComment, setUnassignedBonusComment] = useState<string>("");
    const [unassignedBonusReference, setUnassignedBonusReference] = useState<string>("");
    const [isSaving, setIsSaving] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);
    const [showPreview, setShowPreview] = useState<boolean>(false);
    const [isAssigning, setIsAssigning] = useState(false);

    const [deductionDialogOpen, setDeductionDialogOpen] = useState(false);
    const [pendingDeductionIndex, setPendingDeductionIndex] = useState<number | null>(null);

    const memoizedJobBuilders = useMemo(() => jobBuilders || [], [jobBuilders]);
    const memoizedAllBuilders = useMemo(() => allBuilders || [], [allBuilders]);

    const userRoles = (accounts[0]?.idTokenClaims?.roles as string[]) || [];
    const hasOpsPlusRole =
        userRoles.includes("OpsPlus") || role === "OpsPlus" || role === "Opsplus";

    const isModelHome = job?.jobTypeCode === "M";
    const currentClosingBonus = job?.bonusDetail?.bonusAmounts?.closingBonus || 0;
    const currentStage5Bonus = job?.bonusDetail?.bonusAmounts?.stage5Bonus || 0;
    const previewClosingBonus = currentClosingBonus + currentStage5Bonus;
    const previewStage5Bonus = 0;
    const showAssignButton =
        hasOpsPlusRole &&
        isModelHome &&
        bonusType === "ClosingBonus" &&
        currentStage5Bonus > 0;

    const currentBonusAmount =
        bonusType === "Stage5Bonus"
            ? job?.bonusDetail?.bonusAmounts?.stage5Bonus || 0
            : job?.bonusDetail?.bonusAmounts?.closingBonus || 0;

    const canEdit = active && !viewOnly;

    const handleOpen = async () => {
        if (!active) {
            alert(
                viewOnly
                    ? "Not allowed to edit a job in view only mode"
                    : "Not allowed to edit a job actively in review"
            );
            return;
        }

        setOpen(true);
        setIsInitializing(true);

        try {
            const initialRows: BuilderSplitRow[] = (memoizedJobBuilders || []).map((b) => ({
                ...b,
                splitAmount: computeSplitAmount(currentBonusAmount, b.split || 0),
                deductionComment: "",
                deductionConfirmed: false,
            }));

            let hydratedRows = initialRows;
            let cleanBaseComments = "";
            let parsedUnassignedBonusComment = "";
            let parsedUnassignedBonusReference = "";

            try {
                const commentsResponse = await httpClient.get(`/api/cba/comments/${job.jobId}`);
                const comments: BuilderSplitCommentRecord[] = Array.isArray(commentsResponse.data)
                    ? commentsResponse.data
                    : [];

                const latestRelevant = getLatestRelevantBuilderSplitComment(comments);

                if (latestRelevant?.comment) {
                    const parsed = parseBuilderSplitComment(latestRelevant.comment);
                    hydratedRows = hydrateRowsWithParsedComments(initialRows, parsed);
                    cleanBaseComments = latestRelevant.comment || "";
                    parsedUnassignedBonusComment = parsed.unassignedBonusJustification || "";
                    parsedUnassignedBonusReference = parsed.unassignedBonusReference || "";
                }
            } catch (commentsError) {
                console.warn("Unable to load/parse builder split comments:", commentsError);
            }

            setBuilderSplits(hydratedRows);
            setOriginalBuilderSplits(createBuilderSplitSnapshot(hydratedRows));
            setBaseComments(cleanBaseComments);
            setUnassignedBonusComment(parsedUnassignedBonusComment);
            setUnassignedBonusReference(parsedUnassignedBonusReference);
        } finally {
            setIsInitializing(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setBuilderSplits([]);
        setOriginalBuilderSplits([]);
        setBaseComments("");
        setUnassignedBonusComment("");
        setUnassignedBonusReference("");
        setShowPreview(false);
        setDeductionDialogOpen(false);
        setPendingDeductionIndex(null);
    };

    const handleAssignStage5ToClosing = async () => {
        if (
            !window.confirm(
                "Are you sure you want to assign Stage5 Bonus to Closing Bonus? This action cannot be undone."
            )
        ) {
            return;
        }

        setIsAssigning(true);
        try {
            await httpClient.post("/api/cba/bonus/assign-stage5-to-closing", {
                JobId: job.jobId,
                UpdatedBy: accounts[0].username,
            });
            alert("Stage5 Bonus has been successfully assigned to Closing Bonus.");
            handleClose();
            window.location.reload();
        } catch (error: any) {
            console.error("Failed to assign Stage5 to Closing Bonus:", error);
            alert(
                error.response?.data?.message ||
                "An error occurred while assigning Stage5 to Closing Bonus. Please try again."
            );
        } finally {
            setIsAssigning(false);
        }
    };

    const handleAddBuilder = () => {
        const newBuilder: BuilderSplitRow = {
            builderId: 0,
            jobId: job.jobId,
            builderName: "",
            builderNumber: 0,
            employeeNumber: 0,
            builderEmail: "",
            split: 0,
            splitAmount: 0,
            deductionAmount: 0,
            deductionComment: "",
            deductionConfirmed: false,
            createdOn: "",
            createdBy: "",
            updatedOn: "",
            updatedBy: "",
            isActive: false,
            bonusTypeId: bonusType === "Stage5Bonus" ? 1 : 2,
            isAutoApprovalEligible: false,
        };

        setBuilderSplits((prev) => [...prev, newBuilder]);
    };

    const handleRemoveBuilder = (index: number) => {
        setBuilderSplits((prev) => prev.filter((_, idx) => idx !== index));
    };

    const handleBuilderChange = (index: number, employeeNumber: number) => {
        setBuilderSplits((prev) =>
            prev.map((builderSplit, idx) => {
                if (idx !== index) return builderSplit;

                const selectedBuilder = memoizedAllBuilders.find(
                    (builder) => builder.employeeNumber === employeeNumber
                );

                if (!selectedBuilder) return builderSplit;

                return {
                    ...builderSplit,
                    builderId: selectedBuilder.builderId,
                    builderName: selectedBuilder.builderName,
                    builderEmail: selectedBuilder.builderEmail.toLowerCase(),
                    builderNumber: selectedBuilder.builderNumber,
                    employeeNumber: selectedBuilder.employeeNumber,
                    deductionComment:
                        builderSplit.employeeNumber !== selectedBuilder.employeeNumber
                            ? ""
                            : builderSplit.deductionComment,
                    deductionConfirmed:
                        builderSplit.employeeNumber !== selectedBuilder.employeeNumber
                            ? false
                            : builderSplit.deductionConfirmed,
                };
            })
        );
    };

    const handleSplitChange = (index: number, split: number) => {
        setBuilderSplits((prev) =>
            prev.map((builderSplit, idx) =>
                idx === index
                    ? {
                        ...builderSplit,
                        split: Math.max(0, Number.isFinite(split) ? split : 0),
                        splitAmount: computeSplitAmount(
                            currentBonusAmount,
                            Math.max(0, Number.isFinite(split) ? split : 0)
                        ),
                    }
                    : builderSplit
            )
        );
    };

    const handleDeductionChange = (index: number, amount: number) => {
        const safeAmount = Math.max(0, Number.isFinite(amount) ? amount : 0);

        let shouldOpenDialog = false;

        setBuilderSplits((prev) =>
            prev.map((row, idx) => {
                if (idx !== index) return row;

                const clampedAmount = Math.min(safeAmount, row.splitAmount || 0);
                const existingAmount = row.deductionAmount || 0;
                const hasComment = !!row.deductionComment?.trim();

                if (clampedAmount > 0 && existingAmount === 0 && !hasComment) {
                    shouldOpenDialog = true;
                }

                return {
                    ...row,
                    deductionAmount: clampedAmount,
                };
            })
        );

        if (shouldOpenDialog) {
            setPendingDeductionIndex(index);
            setDeductionDialogOpen(true);
        }
    };

    const handleEditDeductionComment = (index: number) => {
        setPendingDeductionIndex(index);
        setDeductionDialogOpen(true);
    };

    const handleDeductionDialogContinue = (comment: string) => {
        if (pendingDeductionIndex === null) return;

        setBuilderSplits((prev) =>
            prev.map((row, idx) =>
                idx === pendingDeductionIndex
                    ? {
                        ...row,
                        deductionComment: comment,
                        deductionConfirmed: true,
                    }
                    : row
            )
        );

        setDeductionDialogOpen(false);
        setPendingDeductionIndex(null);
    };

    useEffect(() => {
        if (!open) return;

        setBuilderSplits((prev) =>
            prev.map((row) => ({
                ...row,
                splitAmount: computeSplitAmount(currentBonusAmount, row.split || 0),
                deductionAmount: Math.min(row.deductionAmount || 0, computeSplitAmount(currentBonusAmount, row.split || 0)),
            }))
        );
    }, [bonusType, currentBonusAmount, open]);

    const pendingDeductionRow =
        pendingDeductionIndex !== null ? builderSplits[pendingDeductionIndex] : undefined;

    const handleContinue = () => {
        if (!builderSplits.length) {
            alert("Please select a builder");
            return;
        }

        const hasNoName = builderSplits.some((builderSplit) => builderSplit.builderName === "");
        if (hasNoName) {
            alert("Missing builder name");
            return;
        }

        const hasZeroSplit = builderSplits.some((builderSplit) => builderSplit.split === 0);
        if (hasZeroSplit) {
            alert("All builders must have a split greater than 0%.");
            return;
        }

        const totalSplit = builderSplits.reduce((acc, curr) => acc + curr.split, 0);
        if (builderSplits.length && totalSplit !== 100) {
            alert("Total split must equal 100%.");
            return;
        }

        const emailSet = new Set<string>();
        const hasDuplicateNames = builderSplits.some((builder) => {
            const email = (builder.builderEmail || "").toLowerCase();
            if (emailSet.has(email)) return true;
            emailSet.add(email);
            return false;
        });

        if (hasDuplicateNames) {
            alert("Duplicate builder names detected. Each builder must be unique.");
            return;
        }

        const changed = hasAnyMeaningfulChanges(builderSplits, originalBuilderSplits);
        if (!changed) {
            alert("No changes detected.");
            return;
        }

        const recalculatedRows = builderSplits.map((builder) => ({
            ...builder,
            splitAmount: computeSplitAmount(currentBonusAmount, builder.split),
        }));

        const totalSplitAmountRaw = recalculatedRows.reduce(
            (acc, row) => acc + ((currentBonusAmount * row.split) / 100),
            0
        );

        const totalSplitAmountRounded = recalculatedRows.reduce(
            (acc, row) => acc + (row.splitAmount || 0),
            0
        );

        if (totalSplitAmountRaw - currentBonusAmount > 0.01) {
            alert(
                `Total bonus amount cannot exceed $${currentBonusAmount.toFixed(
                    2
                )}. Current total: $${totalSplitAmountRounded.toFixed(2)}`
            );
            return;
        }

        if (hasInvalidDeduction(recalculatedRows)) {
            alert("Deduction amount cannot exceed the builder's split amount.");
            return;
        }

        const changedDeductionRows = getDeductionChanges(
            builderSplits,
            originalBuilderSplits
        );

        const rowsMissingDeductionComment = builderSplits.filter(
            (b) =>
                (b.deductionAmount || 0) > 0 &&
                !b.deductionConfirmed &&
                (b.deductionComment?.trim()?.length ?? 0) < 3
        );

        if (rowsMissingDeductionComment.length > 0) {
            alert("CBA — Deduction Not Confirmed: Each builder with a deduction must have an explanation.");
            return;
        }

        setIsSaving(true);

        const commentsPayload = buildStructuredBuilderSplitComment({
            baseComments,
            deductions: builderSplits.map((row) => ({
                builderName: row.builderName,
                employeeNumber: row.employeeNumber,
                deductionAmount: row.deductionAmount || 0,
                deductionComment: row.deductionComment,
            })),
            unassignedBonusJustification: unassignedBonusComment,
            unassignedBonusReference: unassignedBonusReference,
        });

        httpClient.post("/api/cba/builders/bulk", {
            JobId: job.jobId,
            IsUpdateByReviewer: false,
            UpdatedBy: accounts[0].username,
            Comments: commentsPayload,
            BulkUpdateBuildersRequests: recalculatedRows.map((builder) => ({
                JobId: job.jobId,
                BuilderId: builder.builderId,
                BuilderName: builder.builderName,
                BuilderEmail: builder.builderEmail,
                EmployeeNumber: builder.employeeNumber,
                Split: builder.split,
                SplitAmount: builder.splitAmount,
                DeductionAmount: builder.deductionAmount || 0,
                CreatedBy: accounts[0].username,
            })),
        })
            .then(() => {
                handleClose();
                reloadBuilders();
                window.location.reload();
            })
            .catch((error) => {
                console.error("Failed to save builder splits:", error);
                alert("An error occurred while saving. Please try again.");
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    return (
        <>
            <div
                onClick={handleOpen}
                className="cursor-pointer rounded-lg border border-gray-200 p-4 shadow-sm transition duration-300 ease-in-out hover:border-primary-500 hover:bg-gray-50"
            >
                <div className="flex items-center justify-between">
                    <div>
                        <div className="mb-1 text-sm text-gray-500">Builder</div>
                        <div className="font-medium text-gray-900">
                            {memoizedJobBuilders.length
                                ? memoizedJobBuilders.map((b) => b.builderName).join(", ")
                                : "Unassigned"}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="mb-1 text-sm text-gray-500">Split</div>
                        <div className="font-medium text-gray-900">
                            {memoizedJobBuilders.length
                                ? memoizedJobBuilders.map((b) => `${b.split}%`).join(" / ")
                                : "Unassigned"}
                        </div>
                    </div>
                </div>
            </div>

            {open && (
                <>
                    <div className="fixed inset-0 z-50 overflow-y-auto">
                        <div className="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                            </div>

                            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
                                &#8203;
                            </span>

                            <div className="inline-block w-full transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:align-middle">
                                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mt-3 w-full text-center sm:ml-4 sm:mt-0 sm:text-left">
                                            <div className="flex justify-between items-center">
                                                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                                    Builder Split -{" "}
                                                    {bonusType === "Stage5Bonus" ? "Stage 5 Bonus" : "Closing Bonus"}
                                                    <span style={{ color: "#fff" }}> - {builderSplits[0]?.jobId ?? job.jobId ?? ""}</span>
                                                </h3>

                                                {showAssignButton && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowPreview(!showPreview)}
                                                        className="inline-flex items-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                                    >
                                                        {showPreview
                                                            ? "Hide Preview"
                                                            : "Show Preview: Assign Stage5 → Closing Bonus"}
                                                    </button>
                                                )}
                                            </div>

                                            {showAssignButton && showPreview && (
                                                <div className="mt-3 mb-3 rounded-md border border-blue-200 bg-blue-50 p-4">
                                                    <h4 className="mb-2 text-sm font-semibold text-blue-900">
                                                        Preview of Changes:
                                                    </h4>

                                                    <div className="space-y-2 text-sm">
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-700">Current Closing Bonus:</span>
                                                            <span className="font-medium">
                                                                $
                                                                {currentClosingBonus.toLocaleString(undefined, {
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2,
                                                                })}
                                                            </span>
                                                        </div>

                                                        <div className="flex justify-between">
                                                            <span className="text-gray-700">Current Stage5 Bonus:</span>
                                                            <span className="font-medium">
                                                                $
                                                                {currentStage5Bonus.toLocaleString(undefined, {
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2,
                                                                })}
                                                            </span>
                                                        </div>

                                                        <div className="mt-2 border-t border-blue-300 pt-2">
                                                            <div className="flex justify-between font-semibold text-green-700">
                                                                <span>New Closing Bonus:</span>
                                                                <span>
                                                                    $
                                                                    {previewClosingBonus.toLocaleString(undefined, {
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2,
                                                                    })}
                                                                </span>
                                                            </div>

                                                            <div className="flex justify-between font-semibold text-red-700">
                                                                <span>New Stage5 Bonus:</span>
                                                                <span>
                                                                    $
                                                                    {previewStage5Bonus.toLocaleString(undefined, {
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2,
                                                                    })}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        onClick={handleAssignStage5ToClosing}
                                                        disabled={isAssigning}
                                                        className="mt-3 inline-flex w-full items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        {isAssigning ? "Assigning..." : "Assign Stage5 → Closing Bonus"}
                                                    </button>
                                                </div>
                                            )}

                                            {isInitializing ? (
                                                <div className="py-10 text-center text-gray-500">Loading builder split...</div>
                                            ) : (
                                                <BuilderSplitTable
                                                    rows={builderSplits}
                                                    allBuilders={memoizedAllBuilders}
                                                    canEdit={canEdit}
                                                    onBuilderChange={handleBuilderChange}
                                                    onSplitChange={handleSplitChange}
                                                    onDeductionChange={handleDeductionChange}
                                                    onEditDeductionComment={handleEditDeductionComment}
                                                    onRemoveRow={handleRemoveBuilder}
                                                    onAddRow={handleAddBuilder}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <button
                                        type="button"
                                        className="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
                                        onClick={handleContinue}
                                        disabled={isSaving || isInitializing}
                                    >
                                        {isSaving ? "Saving..." : "Save"}
                                    </button>

                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 sm:ml-3 sm:mt-0 sm:w-auto sm:text-sm"
                                        onClick={handleClose}
                                        disabled={isSaving}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <DeductionDialog
                        open={deductionDialogOpen}
                        builderName={pendingDeductionRow?.builderName}
                        initialValue={pendingDeductionRow?.deductionComment || ""}
                        onCancel={() => {
                            setDeductionDialogOpen(false);
                            setPendingDeductionIndex(null);
                        }}
                        onContinue={handleDeductionDialogContinue}
                    />
                </>
            )}
        </>
    );
};

export default BuilderSplitWidget;